import dayjs from 'dayjs';
import * as Yup from 'yup';
import { ValidationError } from 'yup';
import { LimitScopeTypes } from '@tiendanube/common/src/domains/marketing/promotions/LimitScopeTypes';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { Scope, ScopeType } from 'domains/Marketing';

const emptySelectionCategories = 'categories.required';
const emptySelectionProducts = 'products.required';
const emptyValueError = 'emptyValueError';
const discountMaxError = 'discountMaxError';
const minimumQuantityError = 'minimumQuantityError';
const exceedsProductQuantityError = 'exceedsProductQuantityError';
const emptyProductsError = 'emptyProductsError';

const discountValueValidation = Yup.number()
  .typeError(emptyValueError)
  .integer(emptyValueError)
  .min(1, emptyValueError)
  .max(100, discountMaxError);

export const validationSchema = Yup.object().shape({
  categories: Yup.array().when('scopeType', {
    is: 'categories',
    then: Yup.array().min(1, emptySelectionCategories),
  }),
  products: Yup.array().when('scopeType', {
    is: 'products',
    then: Yup.array().min(1, emptySelectionProducts),
  }),
  progressiveDiscounts: Yup.mixed().when('discountForQuantity', {
    is: true,
    then: Yup.array()
      .of(
        Yup.object({
          quantity: Yup.number()
            .typeError(emptyValueError)
            .integer(emptyValueError)
            .min(1, emptyValueError),
          discount: discountValueValidation,
        }),
      )
      .test(function (value) {
        if (!value) return true;
        const quantities = value.map((item) => item.quantity);
        const discounts = value.map((item) => item.discount);

        const findDuplicatesIndexes = (arr) => {
          const seen = new Set();
          const duplicates = new Set();
          arr.forEach((item, i) => {
            const value =
              typeof item === 'object'
                ? JSON.stringify(item, Object.keys(item).sort())
                : item;
            if (seen.has(value)) {
              duplicates.add(i);
            } else {
              seen.add(value);
            }
          });
          return duplicates;
        };

        const duplicatePromotionIndexes = findDuplicatesIndexes(value);
        const duplicateQuantitiesIndexes = findDuplicatesIndexes(quantities);
        const duplicateDiscountsIndexes = findDuplicatesIndexes(discounts);

        duplicatePromotionIndexes.forEach((i) => {
          duplicateQuantitiesIndexes.delete(i);
          duplicateDiscountsIndexes.delete(i);
        });

        const errors: ValidationError[] = [];

        duplicatePromotionIndexes.forEach((index) => {
          errors.push(
            this.createError({
              path: `${this.path}[${index}]`,
              message: 'repeatedProgressiveDiscountError',
            }),
          );
        });

        duplicateQuantitiesIndexes.forEach((index) => {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].quantity`,
              message: 'repeatedValueError',
            }),
          );
        });

        duplicateDiscountsIndexes.forEach((index) => {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].discount`,
              message: 'repeatedValueError',
            }),
          );
        });

        if (errors.length > 0) {
          return new ValidationError(errors);
        }

        return true;
      }),
  }),
  buyXPayY: Yup.mixed().when('promotionType', {
    is: PromotionTypes.BUY_X_PAY_Y,
    then: Yup.object({
      buy: Yup.number()
        .typeError(minimumQuantityError)
        .integer(minimumQuantityError)
        .min(2, minimumQuantityError),
      pay: Yup.number()
        .typeError(emptyValueError)
        .integer(emptyValueError)
        .min(1, emptyValueError)
        .when('buy', {
          is: (value) => value && value > 1,
          then: Yup.number()
            .typeError(emptyValueError)
            .integer(emptyValueError)
            .min(1, emptyValueError)
            .lessThan(Yup.ref('buy'), exceedsProductQuantityError),
        }),
    })
      .typeError(emptyProductsError)
      .test(
        'checkBothFieldsEmpty',
        emptyProductsError,
        (_, ctx) => !!ctx.parent.buyXPayY.buy || !!ctx.parent.buyXPayY.pay,
      ),
  }),
  rewards: Yup.array().when('promotionType', {
    is: PromotionTypes.CROSS_SELLING,
    then: Yup.array().of(
      Yup.object().shape({
        scope_value: Yup.array().min(1, emptyProductsError),
        discount_value: discountValueValidation,
        triggers: Yup.array().of(
          Yup.object().shape({
            scope_value: Yup.array().when(
              'scope_type',
              (scope_type: ScopeType, schema) => {
                if (scope_type === Scope.CATEGORIES) {
                  return schema.min(1, emptySelectionCategories);
                } else if (scope_type === Scope.PRODUCTS) {
                  return schema.min(1, emptySelectionProducts);
                } else {
                  return schema.nullable().notRequired();
                }
              },
            ),
          }),
        ),
      }),
    ),
  }),
  startDate: Yup.string().when('dateType', {
    is: 'limited',
    then: Yup.string().test(
      'formatStartDate',
      'coupons.couponForm.errors.startDate.format',
      (value) => dayjs(value, 'YYYY-MM-DD').isValid(),
    ),
  }),
  endDate: Yup.string().when('dateType', {
    is: 'limited',
    then: Yup.string().test(
      'formatEndDate',
      'coupons.couponForm.errors.endDate.format',
      (value) => dayjs(value, 'YYYY-MM-DD').isValid(),
    ),
  }),
  percentageOffDiscount: Yup.mixed().when('promotionType', {
    is: PromotionTypes.PERCENTAGE_OFF,
    then: discountValueValidation,
  }),
  paymentOptions: Yup.array().when('promotionType', {
    is: PromotionTypes.PERCENTAGE_OFF,
    then: Yup.array().when('paymentOptionsScopeType', {
      is: LimitScopeTypes.LIMITED,
      then: Yup.array().min(1, 'paymentOptions.required'),
      otherwise: Yup.array().notRequired(),
    }),
    otherwise: Yup.array().notRequired(),
  }),
  customLabel: Yup.string()
    .nullable()
    .when('customLabelEnabled', {
      is: true,
      then: Yup.string()
        .nullable()
        .required('errorInvalid')
        .min(1, 'errorMin')
        .max(30, 'errorMax')
        .matches(/^(?!\s*$)(?!.*<[^>]*>)[^\r\n]*$/, 'errorInvalid'),
      otherwise: Yup.string()
        .transform(() => undefined)
        .notRequired(),
    }),
  customLabelEnabled: Yup.boolean(),
  combinesWithQuantityDiscounts: Yup.boolean(),
  combinesWithFreeShipping: Yup.boolean(),
  combinesWithCartAmountDiscounts: Yup.boolean(),
  combinesWithAppDiscounts: Yup.boolean(),
  combinesWithPriceDiscounts: Yup.boolean(),
});
